import ConnextTutorial from './ConnextTutorial';
import UserSettings,{applyAccessibility} from './UserSettings';
import {CallIcon} from './CallUI';
import DeviceCheck from './DeviceCheck';
import AnalyticsPreferences from './AnalyticsPreferences';
import AdminPanel from './AdminPanel';
import hebrewLabel from '../hebrewLabels';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import CityPicker from './CityPicker';
import resolveImageUrl from './profileImageUrl';
import BlockedUsers from './BlockedUsers';


// ── Full interests catalogue ──────────────────────────────────────────────
const INTEREST_CATEGORIES = [
  {
    label: "🎮 גיימינג וטכנולוגיה",
    items: ["גיימינג", "משחקי מחשב", "משחקי קונסולה", "משחקים בנייד", "מציאות מדומה ורבודה", "תכנות", "בינה מלאכותית וטכנולוגיה", "גאדג׳טים", "אבטחת סייבר", "קריפטו ו־Web3"],
  },
  {
    label: "🎬 בידור",
    items: ["סרטים", "סדרות", "אנימה", "סרטים תיעודיים", "סטנדאפ", "פודקאסטים", 'YouTube', "סטרימינג", "תיאטרון", "משחקי קופסה"],
  },
  {
    label: "🎵 מוזיקה",
    items: ["פופ", "רוק", 'היפ־הופ', "מוזיקה אלקטרונית", "ג׳אז", "מוזיקה קלאסית", 'R&B', "מטאל", "אינדי", "נגינה"],
  },
  {
    label: "🏃 ספורט וכושר",
    items: ["חדר כושר והרמת משקולות", "ריצה", "רכיבה על אופניים", "שחייה", "כדורגל", "כדורסל", "טניס", "אומנויות לחימה", "יוגה", "טיולים בטבע"],
  },
  {
    label: "🌿 טבע ושטח",
    items: ["טיולים בטבע", "קמפינג", "טיפוס", "גלישה", "סקי וסנובורד", "דיג", "גינון", "צפרות", "אסטרונומיה", "תרמילאות"],
  },
  {
    label: "🍕 אוכל ושתייה",
    items: ["בישול", "אפייה", "קפה", "יין", "בירות בוטיק", "סושי", "תזונה מהצומח", "אוכל רחוב", "מסעדות שף", "הכנת ארוחות מראש"],
  },
  {
    label: "✈️ טיולים ותרבות",
    items: ["מסעות", "תרמילאות", "טיולי דרכים", "שפות", "היסטוריה", "מוזיאונים", "צילום", "אדריכלות", "התנדבות", "פסטיבלים"],
  },
  {
    label: "📚 למידה ויצירה",
    items: ["קריאה", "כתיבה", "ציור ואיור", "ציור בצבע", "פיסול", "צילום", "יצירת סרטים", "עיצוב", "אופנה", "עשה זאת בעצמך ומלאכת יד"],
  },
  {
    label: "🧘 רווחה ואורח חיים",
    items: ["מדיטציה", "קשיבות", "כתיבת יומן", "אסטרולוגיה", "רוחניות", 'התפתחות אישית', "מינימליזם", "קיימות", "בריאות הנפש", "תזונה"],
  },
  {
    label: "🐾 חיות ומחמד",
    items: ["כלבים", "חתולים", "סוסים", "זוחלים", "ציפורים", "חיים בים", "חיות בר", "הצלת בעלי חיים", "טבעונות", "גן חיות ואקווריום"],
  },
];

// Normalise a currentUser object — handles both camelCase (frontend) and
// snake_case (backend) field names so the form always pre-populates.
const normaliseUser = (u) => ({
  username:         u.username         || '',
  age:              u.age              || '',
  gender:           (u.gender || '').trim().toLowerCase(),
  bio:              u.bio              || '',
  interests:        Array.isArray(u.interests) ? u.interests.map(hebrewLabel) : [],
  preferredAgeRange: u.preferredAgeRange || u.preferred_age_range || { min: 18, max: 60 },
  preferredGender:  (u.preferredGender || u.preferred_gender || '').trim().toLowerCase(),
  city:             u.city             || '',
  phoneNumber:      u.phoneNumber      || u.phone_number || '',
});

const Profile = ({ currentUser, updateProfile }) => {
  const [showTutorial,setShowTutorial]=useState(false);
  const [showSettings,setShowSettings]=useState(false);
  useEffect(()=>{applyAccessibility();},[]);
  const [showDeviceCheck,setShowDeviceCheck]=useState(false);
  const [deviceAvailability,setDeviceAvailability]=useState(null);
  useEffect(()=>{
    if(!currentUser?.id)return;
    let active=true;
    // Read device availability only: never activate capture or play audio at login.
    navigator.mediaDevices?.enumerateDevices?.().then(devices=>{if(active)setDeviceAvailability({camera:devices.some(d=>d.kind==='videoinput'),microphone:devices.some(d=>d.kind==='audioinput')});}).catch(()=>{});
    return()=>{active=false;};
  },[currentUser?.id]);
  const [isProfileVisible, setIsProfileVisible] = useState(false);
  const [profileData, setProfileData] = useState(() => normaliseUser(currentUser));
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const photoRequest = useRef(0);
  useEffect(() => {
    let active = true;
    const request = ++photoRequest.current;
    setProfileImageUrl('');
    // Always use the account's saved photo; the old global cache could be stale.
    localStorage.removeItem('profileImageUrl');
    if (currentUser.id) {
      axios.get(`${API_BASE_URL}/api/get_profile_image_url/${currentUser.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      }).then(({ data }) => {
        if (active && photoRequest.current === request && data.success) {
          setProfileImageUrl(resolveImageUrl(data.imageUrl));
        }
      }).catch(() => {});
    }
    return () => { active = false; };
  }, [currentUser.id]);

  // ── Sync form when currentUser changes ───────────────────────────────────
  useEffect(() => {
    setProfileData(normaliseUser(currentUser));
  }, [currentUser]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setProfileData(prev => ({
        ...prev,
        interests: checked
          ? [...prev.interests, value]
          : prev.interests.filter(i => i !== value),
      }));
    } else if (name === 'min' || name === 'max') {
      setProfileData(prev => ({
        ...prev,
        preferredAgeRange: { ...prev.preferredAgeRange, [name]: parseInt(value, 10) },
      }));
    } else {
      setProfileData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateProfile(profileData);
    setIsProfileVisible(false);
  };

  // ── Profile picture upload ────────────────────────────────────────────────
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const authToken = localStorage.getItem('token');
    if (!authToken) return;

    ++photoRequest.current;
    setUploadingImage(true);
    setUploadError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_BASE_URL}/upload_image`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (res.data.filename) {
        setProfileImageUrl(resolveImageUrl(res.data.imageUrl || `/uploads/${res.data.filename}`));
      }
    } catch (err) {
      console.error('Image upload error:', err);
      setUploadError("העלאת התמונה נכשלה. נסו שוב.");
    } finally {
      setUploadingImage(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`user-dashboard-form ${isProfileVisible ? 'open' : 'closed'}`}>
      {/* Avatar button */}
      <button
        type="button"
        className="profile-avatar-button"
        onClick={() => setIsProfileVisible(v => !v)}
        aria-label={isProfileVisible ? "סגירת הפרופיל" : "עריכת פרופיל"}
      >
        <img
          className="profile-image"
          src={profileImageUrl || '/logo192.png'}
          alt="פרופיל"
          onError={(e) => { e.target.onerror = null; e.target.src = '/logo192.png'; }}
        />
        {!isProfileVisible && <span className="profile-avatar-ring" />}
      </button>

      {showTutorial && <ConnextTutorial onClose={()=>setShowTutorial(false)}/>}
      {showSettings && <UserSettings user={currentUser} onClose={()=>setShowSettings(false)} onEdit={()=>{setShowSettings(false);setIsProfileVisible(true);}}/>}
      {isProfileVisible && (
        <div className="profile-panel">
          <div className="profile-quick-actions" role="group" aria-label="הגדרות והדרכה">
            <button type="button" aria-label="הגדרות משתמש" title="הגדרות משתמש" onClick={()=>setShowSettings(true)}><svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m9 3 6 0 .6 3 2 .9 2.8-.9 3 5.2-2.2 2.1v2.3l2.2 2.1-3 5.2-2.8-.9-2 .9-.6 3H9l-.6-3-2-.9-2.8.9-3-5.2L2.8 14v-2.3L.6 9.6l3-5.2 2.8.9 2-.9Z" transform="translate(2 0) scale(.83)"/><circle cx="12" cy="12" r="3"/></svg></button>
            <button type="button" aria-label="איך זה עובד? הדרכה" title="הדרכה" onClick={()=>setShowTutorial(true)}>?</button>
          </div>

          {/* ── Header ── */}
          <div className="profile-panel-header">
            <div className="profile-title-block" style={{ textAlign: 'center', width: '100%' }}>
              <span className="profile-kicker">הפרופיל שלי</span>
              <h2>עריכת פרטים</h2>
              <p>עדכנו את הפרטים כדי שנוכל למצוא התאמות מתאימות יותר.</p>
            </div>
            <button
              type="button"
              className="profile-close-button"
              onClick={() => setIsProfileVisible(false)}
              aria-label="סגירה"
            >
              ×
            </button>
          </div>

          <div className="profile-device-tools">
            <button type="button" className="profile-device-button" aria-label="בדיקת מצלמה, מיקרופון ורמקולים" aria-expanded={showDeviceCheck} aria-controls="profile-device-check" onClick={()=>setShowDeviceCheck(v=>!v)}>
              <span className="profile-device-icons" aria-hidden="true"><CallIcon name="video"/><CallIcon name="mic"/><CallIcon name="volume"/></span>
              <span className="profile-device-copy"><strong>מוכנים לשיחה?</strong><small>בדיקת מצלמה, מיקרופון ורמקולים</small></span>
              <span className="profile-device-action">{showDeviceCheck?'סגירה':'בדיקה'} <span aria-hidden="true">{showDeviceCheck?'−':'←'}</span></span>
            </button>
            {showDeviceCheck&&<div id="profile-device-check">
              {deviceAvailability&&(!deviceAvailability.camera||!deviceAvailability.microphone)&&<p>לא זוהו כל המכשירים בבדיקה הראשונית. הבדיקה הידנית תאפשר לבדוק חיבור והרשאות.</p>}
              <DeviceCheck standalone onCancel={()=>setShowDeviceCheck(false)}/>
            </div>}
          </div>
          <form onSubmit={handleSubmit} className="profile-form">

            {/* ── Profile Picture ── */}
            <section className="profile-section">
              <h3>תמונת פרופיל</h3>
              <div className="profile-photo-row">
                <div className="profile-photo-preview">
                  <img
                    src={profileImageUrl || '/logo192.png'}
                    alt="פרופיל"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/logo192.png'; }}
                  />
                  {uploadingImage && <div className="profile-photo-uploading">⏳</div>}
                </div>
                <div className="profile-photo-actions">
                  <button
                    type="button"
                    className="profile-photo-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? 'מעלים…' : "📷 החלפת תמונה"}
                  </button>
                  <p className="profile-photo-hint">JPG, PNG או GIF · עד 25 MB</p>
                  {uploadError && <p className="profile-photo-error">{uploadError}</p>}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  aria-label="בחירת תמונת פרופיל"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                />
              </div>
            </section>

            {/* ── Personal Details ── */}
            <section className="profile-section">
              <h3>פרטים אישיים</h3>
              <div className="profile-grid">
                <label className="profile-field">
                  <span>שם משתמש</span>
                  <input
                    type="text"
                    name="username"
                    value={profileData.username}
                    onChange={handleChange}
                    placeholder="שם המשתמש שלכם"
                  />
                </label>

                <label className="profile-field">
                  <span>גיל</span>
                  <input
                    type="number"
                    name="age"
                    value={profileData.age}
                    onChange={handleChange}
                    placeholder="18"
                    min="18"
                  />
                </label>

                <label className="profile-field">
                  <span>מגדר</span>
                  <select name="gender" value={profileData.gender} onChange={handleChange}>
                    <option value="">בחירה…</option>
                    <option value="male">גבר</option>
                    <option value="female">אישה</option>
                    <option value="other">אחר</option>
                  </select>
                </label>

                <label className="profile-field">
                  <span>יישוב</span>
                  <CityPicker value={profileData.city} onChange={handleChange} allowLocation />
                </label>
              </div>

              <label className="profile-field profile-field-full">
                <span>קצת עליי</span>
                <textarea
                  name="bio"
                  value={profileData.bio}
                  onChange={handleChange}
                  placeholder="ספרו קצת על עצמכם…"
                  rows={4}
                />
              </label>
            </section>

            {/* ── Interests ── */}
            <details className="profile-section profile-interests-fold">
              <summary><span>תחומי עניין</span><small>{profileData.interests.length} נבחרו</small></summary>
              <p className="profile-interests-hint">
                {profileData.interests.length} נבחרו — בחרו כל מה שמתאים לכם
              </p>
              <div className="profile-interests-categories">
                {INTEREST_CATEGORIES.map(cat => (
                  <div key={cat.label} className="profile-interest-category">
                    <p className="profile-interest-category-label">{cat.label}</p>
                    <div className="interest-chip-list">
                      {cat.items.map(interest => (
                        <label className="interest-chip" key={interest}>
                          <input
                            type="checkbox"
                            name="interests"
                            value={interest}
                            checked={profileData.interests.includes(interest)}
                            onChange={handleChange}
                          />
                          <span>{interest}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>

            {/* ── Match Preferences ── */}
            <section className="profile-section">
              <h3>העדפות היכרות</h3>
              <div className="profile-grid">
                <label className="profile-field">
                  <span>אשמח להכיר…</span>
                  <select name="preferredGender" value={profileData.preferredGender} onChange={handleChange}>
                    <option value="">בחירה…</option>
                    <option value="male">גברים</option>
                    <option value="female">נשים</option>
                    <option value="other">אחר</option>
                  </select>
                </label>

                <label className="profile-field">
                  <span>מספר טלפון</span>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={profileData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+1 555 000 0000"
                  />
                </label>
              </div>

              <div className="age-range-card">
                <div className="age-range-heading">
                  <span>טווח גילאים מועדף</span>
                  <strong>{profileData.preferredAgeRange.min} – {profileData.preferredAgeRange.max}</strong>
                </div>
                <input
                  type="range"
                  name="min"
                  min="18"
                  max="100"
                  value={profileData.preferredAgeRange.min}
                  onChange={handleChange}
                />
                <input
                  type="range"
                  name="max"
                  min="18"
                  max="100"
                  value={profileData.preferredAgeRange.max}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* ── Actions ── */}
            <div className="profile-actions">
              <button type="button" className="profile-secondary-button" onClick={() => setIsProfileVisible(false)}>
                ביטול
              </button>
              <button type="submit" className="profile-submit-button">
                שמירת פרופיל
              </button>
            </div>
          </form>
          <BlockedUsers key={currentUser.id} />
          <AnalyticsPreferences />
          <AdminPanel key={`admin-${currentUser.id}`} token={localStorage.getItem('token')} />
        </div>
      )}
    </div>
  );
};

export default Profile;
